import React, { FC, useCallback, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { captureException } from '@sentry/node';
import PopupResult from '@ertelecom/ui-react/components/popups/popupResult';
// @ts-ignore
import { CreatablePhoneSelect } from '@ertelecom/ui-react/components/select/creatableSelect';
import { PopupAuthChange } from '~/src/components/Index/PresentsSlider/styled';
import PopupRequestByCall from '~/src/components/Popups/RequestByCall';
import AuthForm from '~/src/components/AuthForm';
import { buyItem, purchaseAdditionalServices } from '~/src/api/shop';
import { selectDomain } from '~/src/store/selectors/domain';
import { TState } from '~/src/store';
import { NON_BREAKING_SPACE } from '~/src/constants/simbols';
import { EShopCategory, IShopItem } from '~/src/api/content/shop/getShopItems';
import { getContacts } from '~/src/api/profile/getContacts';
import { getRALifeTime, monthText, showOfferLink } from './helpers';
import * as ST from './styled';
import { dataLayerPush } from '@ertelecom/ui-react/components/helpers';

const THEME = {
    flexboxgrid: {
        breakpoints: {
            xs: 0,
            sm: 48,
            md: 80,
            lg: 100,
        },
    },
};

const ACTIONS = {
    click: 'click on button',
    activation: 'plus activation',
};

interface IPhone {
    value: number,
    label: string
}

interface IPrices {
    leasing: any[],
    possess: number,
    rent: {
        month: {
            value: number,
            lifetime_price: number,
        },
        year: number | null,
    },
}

interface IPricesV2 {
    date_close_promo: string | null,
    duration: number | null,
    id: number,
    lifetime_price: number | null,
    owner_id: number,
    price_type: string,
    value: number,
}

export interface IPromoItem extends IShopItem {
    extra: any[],
    isShop?: number,
    prices: IPrices,
    pricesPromo: IPrices,
    pricesV2: IPricesV2[],
    stepId?: number,
    color_background?: string,
    colorBackground?: string,
}

type ICardProps = PropsFromRedux & {
    headerBlock?: JSX.Element,
    imageBlock?: JSX.Element,
    titleBlock?: JSX.Element,
    descriptionBlock?: JSX.Element,
    purchaseBlock?: JSX.Element,
    item: IPromoItem,
    isCarousel?: boolean,
    display: string,
    oneRow: boolean,
    dataTest?: string,
    returnUrl?: string,
    isYandex?: boolean,
}

const CREATED_VALUE = -1;
const YANDEX_TRIAL_DURATION = 1;

const PromoCard: FC<ICardProps> = ({
                                       auth,
                                       city,
                                       billingDomain,
                                       imageBlock,
                                       titleBlock,
                                       descriptionBlock,
                                       purchaseBlock,
                                       dataTest,
                                       display = 'block',
                                       oneRow = false,
                                       item,
                                       returnUrl,
                                       isCarousel = false,
                                       accessToken,
                                       isYandex,
                                   }) => {
    const {
        id,
        exists,
        isShop,
        pricesPromo,
        title,
        body,
        labels,
        costRa,
        color_background,
        colorBackground,
        button_link: buttonLink,
        body_full: bodyFull,
        prices: {
            possess: pricePossess,
            rent: priceRent,
        } = {},
        category: {
            name: categoryName,
            button_text_exists: buttonTextExists,
            buy_by_call: buyByCall,
            buy_with_check_phone: buyWithCheckPhone,
        } = {},
    } = item;

    const buttonText = item.button_text || item.category?.button_text;

    const [orderStatus, setOrderStatus] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [isYandexTrial, setIsYandexTrial] = useState(false);

    const initialResult = {
        hasResult: false,
        title: '',
        message: '',
        status: '',
    };

    const [popupResult, setPopupResult] = useState({ ...initialResult });
    const [phoneNumber, setPhoneNumber] = useState({
        value: '',
    });
    const [phoneList, setPhoneList] = useState<IPhone[]>([]);
    const [phone, setPhone] = useState<IPhone>({
        value: 0,
        label: '',
    });
    const [showAuthPopup, setShowAuthPopup] = useState(false);
    const [showRequestPopup, setShowRequestPopup] = useState(false);

    const getParams = returnUrl
        ? `?referrer=${encodeURIComponent(returnUrl)}&forceRedirect=true`
        : '';

    const handleClose = () => {
        setIsOpen(false);
    };

    const onPopup = useCallback(({ status = 'error', title = 'Попробуйте позже', message }) => {
        setPopupResult({
            hasResult: true,
            title,
            message,
            status,
        });
        setOrderStatus(status === 'success');
        setIsOpen(false);
    }, []);

    const sendYandexEvent = (action: string, label: string) => {
        dataLayerPush({
            event: 'UAevent',
            category: 'yandex_plus',
            action,
            label,
        });
    };

    const getButtonText = () => {
        if (!exists) {
            return orderStatus ? buttonTextExists : buttonText;
        }

        return buttonTextExists;
    };

    const onConnectClick = () => {
        if (buttonLink) {
            showOfferLink(buttonLink);
            return;
        }

        if (isYandex) {
            sendYandexEvent(ACTIONS.click, 'button usnat podrobnee');
        }

        if (auth.isAuth || buyByCall) {
            if (buyWithCheckPhone || buyByCall && bodyFull) {
                setIsOpen(true);
            } else {
                handleOrder();
            }
        } else {
            setShowAuthPopup(true);
        }
    };

    const checkYandexTrial = () => {
        const cost = costRa?.find(item => item.duration === YANDEX_TRIAL_DURATION);
        if (cost && cost.flag_ra) {
            setIsYandexTrial(true);
        }
    };

    const handleOrder = () => {
        if (buyByCall) {
            setIsOpen(false);
            setShowRequestPopup(true);
            return;
        }

        let flagId: number | undefined;
        if (isYandex && isYandexTrial) {
            flagId = costRa.find(i => i.duration === YANDEX_TRIAL_DURATION)?.flag_ra;
            sendYandexEvent(ACTIONS.click, 'poprobovat besplatno');
        } else if (isYandex && !isYandexTrial) {
            sendYandexEvent(ACTIONS.click, 'podkluchit podpisky');
        }

        const phoneNum = phone.value === CREATED_VALUE ? phoneNumber.value : phone.value;

        const purchaseFunction = buyWithCheckPhone
            ? purchaseAdditionalServices(billingDomain, auth.accessToken, id, phoneNum, flagId)
            : buyItem(billingDomain, auth.accessToken, id);

        purchaseFunction
            .then((response) => {
                onPopup({
                    status: 'success',
                    title: isYandex ? 'Подписка подключена' : 'Приятного пользования!',
                    message: isYandex
                        ? 'Осталось только её активировать. Для этого перейдите в сервисы Яндекса, иначе вы не ' +
                        'сможете начать пользоваться подпиской.'
                        : response.message,
                });
                if (isYandex) {
                    sendYandexEvent(ACTIONS.activation, 'plus has been activated');
                }
            })
            .catch(error => {
                onPopup({
                    message: error.message
                });

                if (error.message.includes('пополните баланс') && isYandex) {
                    sendYandexEvent(ACTIONS.activation, 'error popolnite balans');
                }

                if (isYandex) {
                    sendYandexEvent(ACTIONS.activation, 'error');
                }
            });
    };

    useEffect(() => {
        getContacts(billingDomain, accessToken)
            .then(resp => {
                const items = resp
                    .phone
                    .filter(item => item.status === 'confirmed')
                    .map(item => {
                        return {
                            label: item.value,
                            value: item.id,
                        };
                    });
                setPhoneList(items);
                setPhone(items[0]);
            })
            .catch(e => {
                captureException(e);
            });
        checkYandexTrial();
    }, []);

    if (!isShop) {
        return (
            <ThemeProvider theme={THEME}>
                <ST.Col isCarousel={isCarousel} data-test={dataTest} display={display} oneRow={oneRow}>
                    <ST.Card isCarousel={isCarousel} oneRow={oneRow} bgColor={color_background || colorBackground}>
                        {!!labels?.length && <ST.HeaderBlock>
                            {
                                labels?.map(l => <ST.Label key={l.id} bgColor={l.color}>{l.title}</ST.Label>)
                            }
                        </ST.HeaderBlock>}
                        {imageBlock && <ST.ImageBlock isCarousel={isCarousel}>{imageBlock}</ST.ImageBlock>}
                        <ST.ContentBlock>
                            {titleBlock && <ST.TitleBlock>{titleBlock}</ST.TitleBlock>}
                            {descriptionBlock && <ST.DescriptionBlock>{descriptionBlock}</ST.DescriptionBlock>}
                            {purchaseBlock && <ST.PurchaseBlock>{purchaseBlock}</ST.PurchaseBlock>}
                        </ST.ContentBlock>
                    </ST.Card>
                </ST.Col>
            </ThemeProvider>
        );
    }

    return (
        <>
            <ThemeProvider theme={THEME}>
                <ST.Col isCarousel={isCarousel} data-test={dataTest} display={display} oneRow={oneRow}>
                    <ST.Card isCarousel={isCarousel} oneRow={oneRow} bgColor={color_background}>
                        {!!labels?.length && <ST.HeaderBlock>
                            {
                                labels.map(l => <ST.Label key={l.id} bgColor={l.color}>{l.title}</ST.Label>)
                            }
                        </ST.HeaderBlock>}

                        <ST.ImageBlock isCarousel={isCarousel}>
                            <ST.Pic src={item.image}/>
                        </ST.ImageBlock>

                        <ST.ContentBlock>
                            <ST.TitleBlock>
                                <ST.Title>{title}</ST.Title>

                                {
                                    pricesPromo?.rent?.month &&
                                    <ST.Free>
                                        {pricesPromo.rent.month.lifetime_price > 1 ? 'первые' : 'первый'}
                                        {NON_BREAKING_SPACE}{pricesPromo.rent.month.lifetime_price}
                                        {NON_BREAKING_SPACE}{monthText(pricesPromo.rent.month.lifetime_price)}
                                        {NON_BREAKING_SPACE}{pricesPromo.rent.month.value}₽/мес.
                                    </ST.Free>
                                }

                                {!isYandex && <ST.WrapperPrice>
                                    <ST.Wrapper>
                                        <ST.Price>
                                            {pricePossess || priceRent?.month || priceRent?.year}
                                        </ST.Price>

                                        {
                                            (!isYandex && priceRent?.month || priceRent?.year || pricePossess)
                                                ? <ST.PriceText>
                                                    {'₽'}

                                                    {
                                                        pricePossess
                                                            ? null
                                                            // eslint-disable-next-line max-len
                                                            : (categoryName === EShopCategory.Antiviruses && !priceRent?.month) ? '/год ' : '/мес. '
                                                    }

                                                    {
                                                        pricesPromo.rent.month
                                                            ? <> - c {getRALifeTime(pricesPromo.rent.month.lifetime_price)}-го
                                                                месяца</>
                                                            : null
                                                    }
                                                </ST.PriceText>
                                                : null
                                        }
                                    </ST.Wrapper>
                                </ST.WrapperPrice>}
                            </ST.TitleBlock>

                            <ST.DescriptionBlock>
                                <ST.Description dangerouslySetInnerHTML={{ __html: body }}/>
                            </ST.DescriptionBlock>

                            <ST.PurchaseBlock>
                                <ST.Content>
                                    <ST.Button
                                        type={orderStatus ? 'secondary' : 'primary'}
                                        text={getButtonText()}
                                        onClick={onConnectClick}
                                    />
                                </ST.Content>

                            </ST.PurchaseBlock>
                        </ST.ContentBlock>
                    </ST.Card>
                </ST.Col>
            </ThemeProvider>

            <ST.ConfirmPopup
                isOpen={isOpen}
                onClose={handleClose}
                city={city.currentCity}
                label={'InternetOrder'}
            >
                <ST.ConfirmPopupTitle>{title}</ST.ConfirmPopupTitle>

                <ST.ConfirmPopupDescription isYandex={isYandex} dangerouslySetInnerHTML={{ __html: bodyFull }}/>

                {isYandex && <ST.YandexLink>Подробнее о подписке</ST.YandexLink>}

                {isYandex && <ST.YandexPrice>
                    {isYandexTrial
                        ? `Первый месяц бесплатно, а потом — ${priceRent?.month} ₽/мес.`
                        : `Стоимость подписки — ${priceRent?.month} ₽/мес.`
                    }
                </ST.YandexPrice>}

                <ST.ConfirmPopupFooter>
                    {
                        !buyByCall &&
                        <CreatablePhoneSelect
                            options={phoneList}
                            onChange={setPhone}
                            value={phone}
                            inputValue={phoneNumber.value}
                            onInputChange={setPhoneNumber}
                            placeholder={'Введите телефон'}
                            inputPlaceHolder={'Номер телефона'}
                        />
                    }

                    <ST.PopupButton
                        onClick={handleOrder}
                        text={isYandex ? isYandexTrial ? 'Попробовать бесплатно' : 'Подключить подписку' : 'Подключить'}
                    />
                </ST.ConfirmPopupFooter>
            </ST.ConfirmPopup>

            <PopupResult
                isOpen={popupResult.hasResult}
                onClose={() => {
                    setPopupResult({ ...initialResult });
                }}
                title={popupResult.title}
                text={popupResult.message}
                type={popupResult.status}
            />

            <PopupAuthChange
                isOpen={showAuthPopup}
                onClose={() => setShowAuthPopup(false)}
            >
                <AuthForm getParams={getParams}/>
            </PopupAuthChange>

            <PopupRequestByCall
                isOpen={showRequestPopup}
                onClose={() => setShowRequestPopup(false)}
                city={city}
                texts={{
                    title: 'Оформление заявки на заказ',
                    subtitle: 'Оставьте заявку, и мы вам перезвоним для уточнения деталей',
                }}
            />
        </>
    );
};

const mapStateToProps = (state: TState) => ({
    auth: state.auth,
    accessToken: state.auth.accessToken,
    city: state.city,
    billingDomain: selectDomain(state),
});

const connector = connect(mapStateToProps);

type PropsFromRedux = ConnectedProps<typeof connector>

export default connector(PromoCard);
